import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

// ─── OFF-SCREEN GLB THUMBNAIL GENERATOR ──────────────────────────────────────
// Renders each model ONCE with a single shared WebGL renderer, snapshots it to
// a PNG data-URL, and caches it. This is how the model-picker grid can show a
// live 3D render of every model without spawning one <Canvas>/WebGL context
// per tile (browsers cap contexts at ~16 — 27 sofas would blow past that) and
// without layering a shared canvas through a scrolling modal.

const SIZE = 200; // render/thumb resolution (square)

let renderer = null;
let scene = null;
let camera = null;
let loader = null;

const cache = new Map();   // url → dataURL (or null if it failed)
const pending = new Map(); // url → Promise<dataURL|null>

function init() {
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setSize(SIZE, SIZE);
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x666666, 1.15));
  const key = new THREE.DirectionalLight(0xffffff, 1.9);
  key.position.set(3, 5, 4);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.7);
  fill.position.set(-4, 2, -3);
  scene.add(fill);

  camera = new THREE.PerspectiveCamera(32, 1, 0.05, 100);

  loader = new GLTFLoader();
  // Match drei's default so Draco-compressed models also decode.
  const draco = new DRACOLoader();
  draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
  loader.setDRACOLoader(draco);
}

function disposeModel(model) {
  model.traverse((o) => {
    if (o.isMesh) {
      o.geometry?.dispose?.();
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        if (!m) return;
        Object.values(m).forEach((v) => { if (v && v.isTexture) v.dispose(); });
        m.dispose?.();
      });
    }
  });
}

// Render one model into a data-URL. Framed with a ¾ view so the shape reads.
function renderThumb(model) {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  const s = 2 / maxDim;
  model.scale.setScalar(s);
  model.position.set(-center.x * s, -center.y * s, -center.z * s);
  scene.add(model);

  const dist = 3.1;
  camera.position.set(dist * 0.72, dist * 0.62, dist * 0.9);
  camera.lookAt(0, 0, 0);

  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/png');

  scene.remove(model);
  disposeModel(model);
  return url;
}

// Public: get (and lazily generate + cache) a model's thumbnail data-URL.
// Resolves to null if the model can't be loaded/rendered (caller shows an icon).
export function getModelThumbnail(glbUrl) {
  if (!glbUrl) return Promise.resolve(null);
  if (cache.has(glbUrl)) return Promise.resolve(cache.get(glbUrl));
  if (pending.has(glbUrl)) return pending.get(glbUrl);

  const p = new Promise((resolve) => {
    try {
      if (!renderer) init();
      loader.load(
        glbUrl,
        (gltf) => {
          let url = null;
          try { url = renderThumb(gltf.scene); } catch { url = null; }
          cache.set(glbUrl, url);
          pending.delete(glbUrl);
          resolve(url);
        },
        undefined,
        () => { cache.set(glbUrl, null); pending.delete(glbUrl); resolve(null); }
      );
    } catch {
      cache.set(glbUrl, null);
      pending.delete(glbUrl);
      resolve(null);
    }
  });
  pending.set(glbUrl, p);
  return p;
}
